import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Input,
  Spinner,
  Textarea,
  Toast,
  ToastTitle,
  Tooltip,
  tokens,
  useToastController,
} from '@fluentui/react-components';
import {
  CheckmarkCircle20Filled,
  ChevronLeft20Regular,
  ChevronRight20Regular,
  Dismiss20Regular,
  Document20Regular,
  DocumentBulletList20Regular,
  PanelBottom20Regular,
  PanelRight20Regular,
  Warning20Regular,
} from '@fluentui/react-icons';
import {
  cambiosDelRenglon,
  fuenteActivaDelPrecio,
  puedeVolverAlCatalogo,
  requiereNuevaBusqueda,
  tieneLosDosPrecios,
  vecesElPrecioDelCatalogo,
} from '@supernova/shared';
import type { EditarRenglonResponse } from '@supernova/shared';
import { editConcept } from '../api/quotes.js';
import { nombreProductoActivo } from '../lib/brandingDelProducto.js';
import { ConceptAnalysisView } from './ConceptAnalysisView.js';
import { ProcedimientoDrawer } from './ProcedimientoDrawer.js';
import { PROCEDIMIENTO_VARIANTE_DEFAULT } from '../api/procedimiento.js';
import {
  approveMatrix,
  getApuIaConceptMatrix,
  regenerateConcept,
  rejectMatrix,
  reopenMatrix,
  resolveAuxiliarReceta,
  setConceptSource,
  type ApuIaConcepto,
} from '../api/apuIa.js';
import type { QuoteConcept } from '../api/quotes.js';
import { formatoDeMoneda } from '../lib/money.js';
import { useIsEcosoftStaff } from '../stores/authStore.js';
import { useNovedad, useNovedadStyles } from './Novedad.js';
import { useConfirm } from '../components/ConfirmDialog.js';
import { GLOBAL_TOASTER_ID } from './ThemedProvider.js';

/**
 * Panel lateral (drawer, slide-over sin scrim) con el análisis de precio de UN
 * concepto del presupuesto. Se actualiza al enfocar otra fila del grid.
 *  - Matcheado: de qué catálogo/concepto viene + qué tan parecido es.
 *  - Sintético: construido por IA + contexto; "Cómo lo pensó OPUS ai" y
 *    "Ver memoria de cálculo" abren un MODAL in-app (como la vista de sintéticos).
 *    Client-safe: NUNCA muestra parámetros de calibración (% de factores).
 *  - Sin equivalente: explica POR QUÉ no hay precio y qué se puede hacer.
 */

// Los importes van en LA MONEDA DE LA OBRA, que llega por prop (`moneda`) desde el
// presupuesto. Antes era un '$' fijo con formato es-MX/MXN y el panel de un
// presupuesto chileno mostraba pesos mexicanos. Ver lib/money.ts.
const num = (n: number | null | undefined, d = 4): string =>
  (Number(n) || 0).toLocaleString('es-MX', { maximumFractionDigits: d });

// Texto legible para un chip de contexto: prefiere una frase de la IA; si solo
// hay una clave técnica cruda (ej. "trabajo_exterior_en_temporada_lluvias: true")
// la vuelve legible (quita ": true/false", cambia "_" por espacio, capitaliza).
// Es una transformación, no una tabla de alias.
function contextoLabel(f: { evidencia?: string | null; justificacion?: string | null; id: string }): string {
  const esFrase = (s?: string | null): s is string =>
    !!s && s.trim().includes(' ') && !/^[a-z0-9]+(_[a-z0-9]+)+\s*:/i.test(s.trim());
  if (esFrase(f.justificacion)) return f.justificacion.trim();
  if (esFrase(f.evidencia)) return f.evidencia.trim();
  const raw = (f.evidencia || f.justificacion || f.id).trim();
  const limpio = raw.replace(/:\s*(true|false)\s*$/i, '').replace(/_/g, ' ').trim();
  return limpio.charAt(0).toUpperCase() + limpio.slice(1);
}

function cuadrillaComps(cuad: unknown): Array<{ cantidad: number; categoria: string }> {
  const obj = cuad as { componentes?: Array<{ cantidad?: number; categoria?: string }> } | null | undefined;
  const arr = obj?.componentes ?? [];
  return arr.map((c) => ({ cantidad: Number(c.cantidad ?? 0), categoria: String(c.categoria ?? '') }));
}

// --- "Sin equivalente": qué pasó + qué hacer, en palabras que entienda
// cualquiera (de un niño a un anciano), sin jerga. Devuelve el porqué y los
// pasos concretos. Ver memoria feedback_explicaciones_para_todos.
interface PasoSinPrecio { texto: string; irABuscar?: boolean }
function sinEquivalente(c: QuoteConcept): { porque: string; pasos: PasoSinPrecio[] } {
  // Flujo real de botones: "Buscar equivalentes" (OPUS ai busca en los catálogos y,
  // en la misma corrida, confirma los dudosos con inteligencia artificial) → si no
  // está en tus catálogos, la IA arma el precio. Los pasos con irABuscar son
  // clicables y llevan al paso 2. Siempre "concepto", nunca "trabajo".
  const flujo: PasoSinPrecio[] = [
    { texto: `Haz clic en “Buscar equivalentes” para que ${nombreProductoActivo()} busque este concepto en tus catálogos de referencia.`, irABuscar: true },
    { texto: 'Si no está en tus catálogos, deja que la inteligencia artificial arme su precio.' },
  ];
  if (c.status === 'pending') {
    return { porque: 'Este concepto todavía no tiene precio.', pasos: flujo };
  }
  if (c.status === 'failed') {
    return { porque: 'Todavía no le pudimos poner un precio a este concepto.', pasos: flujo };
  }
  if (c.apuIaRequested) {
    return {
      porque: 'Este concepto todavía no tiene precio: no se encontró un concepto equivalente en tus catálogos de referencia.',
      pasos: [{ texto: 'Ya lo marcaste para que la inteligencia artificial arme su precio. Se construirá la próxima vez que generes los precios con inteligencia artificial.' }],
    };
  }
  if (c.hasCandidates) {
    return {
      porque: 'Este concepto todavía no tiene precio. Se encontraron conceptos parecidos, pero no lo bastante como para tomarlos con confianza.',
      pasos: [
        { texto: 'Deja que la inteligencia artificial arme su precio.' },
        { texto: 'O agrega otro catálogo de referencia y vuelve a “Buscar equivalentes”.', irABuscar: true },
      ],
    };
  }
  return {
    porque: 'Este concepto todavía no tiene precio: no se encontró un concepto equivalente en tus catálogos de referencia.',
    pasos: flujo,
  };
}

/**
 * Cantidad escrita a mano → número. En es-MX la coma es el separador de miles y el punto
 * el decimal, que es como la hoja muestra las cantidades: se aceptan las dos formas de
 * escribirlo ("1,234.5" y "1234.5") y también la coma decimal, porque es lo que hace medio
 * mundo. Devuelve null si lo escrito no es un número.
 */
export function parseCantidad(txt: string): number | null {
  const s = txt.trim().replace(/\s/g, '');
  if (!s) return null;
  // "1.234,56" (coma decimal) → "1234.56". Si hay coma Y punto, el ÚLTIMO manda.
  const ultimaComa = s.lastIndexOf(',');
  const ultimoPunto = s.lastIndexOf('.');
  const limpio = ultimaComa > ultimoPunto
    ? s.replace(/\./g, '').replace(',', '.')
    : s.replace(/,/g, '');
  const n = Number(limpio);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/** Fecha y hora de la corrección, en palabras cortas. */
function cuando(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' })}`
    + ` a las ${d.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`;
}

/** Recorrer los renglones que se están viendo (los del filtro aplicado). */
export interface NavegacionRenglones {
  /** Posición del renglón dentro de la lista visible, empezando en 1. */
  pos: number;
  total: number;
  onPrev: () => void;
  onNext: () => void;
}

interface Props {
  quoteId: string;
  concepto: QuoteConcept;
  /** Nombre del catálogo de origen del match (si aplica). */
  catalogName: string | null;
  /** Veredicto del validador para el match, ya parseado por el contenedor. */
  llmVeredicto: 'si' | 'parcial' | null;
  /** Cotización en curso → deshabilita acciones. */
  isActive: boolean;
  onClose: () => void;
  /** Refrescar el listado tras aceptar/rechazar/reabrir. */
  onChanged: () => void;
  /** Llevar al usuario al paso 2 "Buscar equivalentes": hace scroll arriba y
   *  abre el dropdown del botón. */
  onGoToEquivalentes: () => void;
  /** Posición del panel (estilo Gmail): a la derecha o abajo. */
  mode: 'derecha' | 'abajo';
  /** Cambiar la posición del panel (botones en el header). */
  onModeChange?: (m: 'derecha' | 'abajo') => void;
  /** Tamaño del contenedor: ancho (derecha) o alto (abajo), en px. */
  size: number;
  /** El usuario arrastró el borde para redimensionar. */
  onResize: (n: number) => void;
  /** Caja horizontal del árbol (px). En modo "abajo" el panel se acota a este
   *  ancho/posición para quedar del ancho del árbol (no de toda la fila).
   *  Con la columna de catálogos ya fuera del área scrolleable, el panel vive
   *  dentro de la columna del grid y ocupa su ancho completo → estos son
   *  opcionales (default 0 = stretch). */
  treeLeft?: number;
  treeWidth?: number;
  /** Recorrer los renglones visibles con ← →. Sin esto, los controles no se muestran. */
  nav?: NavegacionRenglones;
  /** ¿Se puede corregir el renglón? (permisos + presupuesto no en marcha). */
  canEdit?: boolean;
  /** El renglón se corrigió: el contenedor avisa y recarga el listado. */
  onEdited?: (r: EditarRenglonResponse<QuoteConcept>) => void;
  /** Moneda de la obra (ISO 4217); `null`/ausente = no se sabe → sin símbolo. */
  moneda?: string | null;
  /** Volver a crear el análisis por otra vía (planos: su propia generación, con las condiciones de obra). */
  onRegenerar?: () => Promise<unknown>;
}

export function ConceptPricePanel({
  quoteId,
  concepto,
  catalogName,
  llmVeredicto,
  isActive,
  onClose,
  onChanged,
  onGoToEquivalentes,
  mode,
  onModeChange,
  size,
  onResize,
  treeLeft = 0,
  treeWidth = 0,
  nav,
  canEdit = false,
  onEdited,
  moneda,
  onRegenerar,
}: Props) {
  const money = useMemo(() => formatoDeMoneda(moneda).money, [moneda]);
  // CUÁL DE LOS DOS PRECIOS CUENTA HOY. Un concepto puede tener a la vez el precio de los
  // catálogos del cliente y el que armó la inteligencia artificial; solo uno entra al total.
  // `isSynthetic` es "hoy manda el de la IA" (no "existe un análisis con IA"): con el
  // precio del catálogo activo, el panel tiene que verse como un renglón de catálogo.
  const isSynthetic = fuenteActivaDelPrecio(concepto) === 'ia';
  /** Existe un análisis con IA guardado, esté activo o no (se puede volver a él en un clic). */
  const tieneAnalisisIa = isSynthetic || concepto.hasApuIaMatrix || !!concepto.apuIaJobRunId;
  const hasMatch = !!(concepto.claveCatalogo || concepto.descripcionCatalogo);
  // ¿Hay dos precios entre los que elegir, y cuál es el que hoy NO cuenta?
  const hayQueElegir = tieneLosDosPrecios(concepto);
  const otroPrecio = isSynthetic ? (concepto.precioCatalogoGuardado ?? null) : (concepto.precioIa ?? null);
  const vecesMasCaro = vecesElPrecioDelCatalogo(
    concepto.precioIa ?? (isSynthetic ? concepto.precioUnitario : null),
    concepto.precioCatalogoGuardado,
  );

  const isStaff = useIsEcosoftStaff();
  // "Novedad": halo + badge en Procedimiento hasta el primer uso (recién abierto al público).
  const novProc = useNovedad('procedimiento');
  const novStyles = useNovedadStyles();
  const confirm = useConfirm();
  const { dispatchToast } = useToastController(GLOBAL_TOASTER_ID);

  const [apu, setApu] = useState<ApuIaConcepto | null>(null);
  const [loadingApu, setLoadingApu] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pensadoOpen, setPensadoOpen] = useState(false);
  const [memoriaOpen, setMemoriaOpen] = useState(false);
  // Panel del Procedimiento constructivo (documento imprimible del concepto).
  const [procedimientoOpen, setProcedimientoOpen] = useState(false);
  const [hoverPensado, setHoverPensado] = useState(false);
  const [hoverProc, setHoverProc] = useState(false);
  // Bump para re-traer la matriz tras resolver un auxiliar.
  const [reloadKey, setReloadKey] = useState(0);
  // Índice del material auxiliar que se está resolviendo (uno a la vez).
  const [resolvingAux, setResolvingAux] = useState<number | null>(null);

  // La matriz (estado de revisión, factores, razonamientos, memoria) se trae siempre que
  // EXISTA un análisis con IA, aunque hoy mande el precio del catálogo: así se puede mostrar
  // el otro precio y volver a él sin recargar nada. Los matcheados puros se pintan desde
  // `concepto`.
  useEffect(() => {
    if (!tieneAnalisisIa) { setApu(null); return; }
    let alive = true;
    setLoadingApu(true);
    getApuIaConceptMatrix(quoteId, concepto.id)
      .then((r) => { if (alive) setApu(r.concepto); })
      .catch(() => { if (alive) setApu(null); })
      .finally(() => { if (alive) setLoadingApu(false); });
    return () => { alive = false; };
  }, [tieneAnalisisIa, quoteId, concepto.id, reloadKey]);

  // Cerrar modales al cambiar de concepto (el contenedor reusa el panel).
  useEffect(() => { setPensadoOpen(false); setMemoriaOpen(false); setProcedimientoOpen(false); }, [concepto.id]);

  // ── CORREGIR EL RENGLÓN A MANO ────────────────────────────────────────────────
  // Sin "modo edición": los tres campos se escriben en el lugar. Guardar y Descartar
  // recién aparecen cuando algo cambió, porque guardar tiene consecuencias (se vuelve a
  // buscar el precio, se mueve el total) y eso tiene que ser deliberado.
  const [borrador, setBorrador] = useState({
    descripcion: concepto.descripcion,
    unidad: concepto.unidad ?? '',
    cantidad: String(concepto.cantidad ?? 0),
  });
  const [guardando, setGuardando] = useState(false);
  const [errorEdicion, setErrorEdicion] = useState<string | null>(null);
  // Al pasar a otro renglón, el borrador arranca del renglón nuevo.
  useEffect(() => {
    setBorrador({
      descripcion: concepto.descripcion,
      unidad: concepto.unidad ?? '',
      cantidad: String(concepto.cantidad ?? 0),
    });
    setErrorEdicion(null);
  }, [concepto.id, concepto.descripcion, concepto.unidad, concepto.cantidad]);

  const cantidadNum = parseCantidad(borrador.cantidad);
  const cambios = useMemo(() => cambiosDelRenglon(
    { descripcion: concepto.descripcion, unidad: concepto.unidad ?? '', cantidad: Number(concepto.cantidad) || 0 },
    { descripcion: borrador.descripcion, unidad: borrador.unidad, cantidad: cantidadNum ?? (Number(concepto.cantidad) || 0) },
  ), [concepto.descripcion, concepto.unidad, concepto.cantidad, borrador, cantidadNum]);
  // La MISMA regla que va a aplicar el servidor (vive en @supernova/shared): el botón
  // tiene que decir lo que realmente va a pasar.
  const seVaABuscar = requiereNuevaBusqueda(cambios, {
    tieneAnalisisIa,
    sinPrecio: Number(concepto.precioUnitario) <= 0,
  });
  const puedeGuardar = canEdit && cambios.hayCambios && !guardando
    && cantidadNum != null && borrador.descripcion.trim().length > 0 && borrador.unidad.trim().length > 0;

  function descartar() {
    setBorrador({
      descripcion: concepto.descripcion,
      unidad: concepto.unidad ?? '',
      cantidad: String(concepto.cantidad ?? 0),
    });
    setErrorEdicion(null);
  }

  async function guardar() {
    if (!puedeGuardar || cantidadNum == null) return;
    setGuardando(true);
    setErrorEdicion(null);
    try {
      const r = await editConcept(quoteId, concepto.id, {
        descripcion: borrador.descripcion.trim(),
        unidad: borrador.unidad.trim(),
        cantidad: cantidadNum,
      });
      onEdited?.(r);
    } catch (e) {
      setErrorEdicion((e as Error).message || 'No pudimos guardar el cambio.');
    } finally {
      setGuardando(false);
    }
  }

  const reviewStatus = apu?.apu_ia_review_status ?? null;
  const factores = (apu?.apu_ia_factores_aplicados ?? []).filter((f) => f.dispara);
  // La marca "Reutilizado de la biblioteca…" no se muestra (decisión: la biblioteca
  // nunca se menciona al usuario).
  const alertas = (apu?.apu_ia_alertas ?? []).filter((a) => !a.startsWith('Reutilizado de la biblioteca'));
  const validaciones = apu?.apu_ia_validaciones ?? [];
  const preguntas = apu?.apu_ia_preguntas_pendientes ?? [];
  const m = apu?.apu_ia_matrix ?? null;
  const razonamientos: Array<{ k: string; v: string }> = [];
  if (m?.razonamiento_alcance) razonamientos.push({ k: 'Alcance', v: m.razonamiento_alcance });
  if (m?.razonamiento_cuadrilla) razonamientos.push({ k: 'Cuadrilla', v: m.razonamiento_cuadrilla });
  if (m?.razonamiento_equipo) razonamientos.push({ k: 'Equipo', v: m.razonamiento_equipo });
  if (m?.razonamiento_desperdicios) razonamientos.push({ k: 'Desperdicios', v: m.razonamiento_desperdicios });
  const tienePensado = razonamientos.length > 0 || !!m?.memoria_decisiones
    || validaciones.length > 0 || alertas.length > 0 || preguntas.length > 0;
  const tieneMemoria = !!m || apu?.apu_ia_costo_directo != null;

  // Teclado del panel (es no-modal; los Dialog manejan su propio Escape):
  //  · Escape descarta los cambios si hay; si no, cierra. Así no se pierde lo escrito
  //    por querer cerrar.
  //  · ← → pasan al renglón anterior/siguiente de la lista que se está viendo. Con el
  //    cursor dentro de un campo NO se usan (ahí las flechas mueven el cursor); para
  //    esos casos está Alt+← / Alt+→, que funciona siempre.
  useEffect(() => {
    const editando = (el: EventTarget | null): boolean => {
      const t = el as HTMLElement | null;
      const tag = t?.tagName?.toLowerCase();
      return tag === 'input' || tag === 'textarea' || t?.isContentEditable === true;
    };
    const onKey = (e: KeyboardEvent) => {
      if (pensadoOpen || memoriaOpen) return;
      if (e.key === 'Escape') {
        if (cambios.hayCambios) descartar();
        else onClose();
        return;
      }
      if (!nav || cambios.hayCambios) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (editando(e.target) && !e.altKey) return;
      e.preventDefault();
      if (e.key === 'ArrowLeft') nav.onPrev();
      else nav.onNext();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, pensadoOpen, memoriaOpen, nav, cambios.hayCambios]);

  // ── ELEGIR CUÁL DE LOS DOS PRECIOS VALE ─────────────────────────────────────
  // Instantáneo, sin inteligencia artificial y sin gastar nada: los dos precios están
  // guardados y esto solo cambia cuál entra al total. Se puede ir y volver las veces que
  // sea. Antes esto existía en el servidor y NINGÚN botón lo llamaba: pedir "cotizar con
  // IA" sobre un concepto que ya tenía precio de catálogo lo perdía para siempre.
  const [cambiandoFuente, setCambiandoFuente] = useState(false);
  async function elegirFuente(source: 'catalog' | 'ia') {
    if (cambiandoFuente) return;
    setCambiandoFuente(true);
    try {
      await setConceptSource(concepto.id, source);
      setReloadKey((k) => k + 1);
      onChanged();
    } finally {
      setCambiandoFuente(false);
    }
  }

  async function run(fn: () => Promise<unknown>) {
    setBusy(true);
    try {
      await fn();
      // Refrescar la matriz/estado del panel (aprobar / rechazar / REABRIR): sin
      // esto reviewStatus quedaba viejo y "Reabrir" parecía no hacer nada.
      setReloadKey((k) => k + 1);
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  // Rechazar el análisis. Si el concepto tenía precio de catálogo antes, el servidor lo
  // vuelve a poner activo (un precio rechazado no puede seguir contando en el total) y acá
  // se le dice, porque el número del renglón cambia delante de sus ojos.
  async function onReject() {
    setBusy(true);
    try {
      const r = await rejectMatrix(concepto.id);
      setReloadKey((k) => k + 1);
      onChanged();
      if (r.precioDeCatalogoRestaurado) {
        dispatchToast(
          <Toast><ToastTitle>Volvimos al precio de tu catálogo</ToastTitle></Toast>,
          { intent: 'success', timeout: 4000 },
        );
      }
    } finally {
      setBusy(false);
    }
  }

  // Volver a crear el análisis de ESTE concepto con IA (consume 1 de cuota).
  // Avisa si hay ediciones manuales que se perderán.
  async function onRegenerate() {
    const avisoEdicion = m?.editado_manual
      ? 'ESTE ANÁLISIS TIENE EDICIONES MANUALES — volver a crearlo con inteligencia artificial las reemplaza.\n\n'
      : '';
    const ok = await confirm({
      title: 'Volver a crear el análisis',
      message: `${avisoEdicion}¿Volver a crear el análisis con inteligencia artificial de este concepto?\n\nSe perderá el análisis actual y se generará uno nuevo. Cuenta como 1 análisis de precio unitario generado en tu cuota.`,
      confirmLabel: 'Regenerar',
    });
    if (!ok) return;
    await run(() => (onRegenerar ? onRegenerar() : regenerateConcept(concepto.id)));
    onClose();
  }

  // Insumos auxiliares que la IA dejó sin composición (sin sub-receta) → el
  // ingeniero puede pedir que la IA arme la matriz del auxiliar.
  const auxiliaresSinResolver = (m?.materiales ?? [])
    .map((mat, i) => ({ mat, i }))
    .filter(({ mat }) => mat.tipo === 'auxiliar' && !mat.sub_receta);

  async function resolverAux(matIdx: number, descripcion: string, unidad: string) {
    setResolvingAux(matIdx);
    try {
      await resolveAuxiliarReceta(concepto.id, { matIdx, descripcion, unidad });
      // La receta nueva llega sin precios y el backend encola el re-costeo, así que hay
      // que ESPERARLO: refrescar de inmediato mostraba el auxiliar en "—" y parecía que
      // resolverlo no había servido de nada. Se espera hasta que el auxiliar tenga su
      // precio (mismo patrón que "Ajustar": reintentos cortos, sin bloquear para siempre).
      for (let i = 0; i < 12; i++) {
        await new Promise((r) => setTimeout(r, 700));
        const r = await getApuIaConceptMatrix(quoteId, concepto.id).catch(() => null);
        const mat = r?.concepto?.apu_ia_matrix?.materiales?.[matIdx] as { precio_unitario?: number | null } | undefined;
        if (mat && mat.precio_unitario != null) break;
      }
      setReloadKey((k) => k + 1); // re-trae la matriz con la sub-receta ya costeada
      onChanged();
    } finally {
      setResolvingAux(null);
    }
  }

  // El panel es HIJO FLEX del área de trabajo (hermano del scroll) → empuja el
  // contenido, nunca lo encima. En "abajo" se acota al ancho del árbol (treeBox);
  // a la "derecha" toma el alto completo del área.
  const drawer: React.CSSProperties = mode === 'abajo'
    ? {
        // Ancho del GRID de conceptos (no toda la fila): se alinea con el árbol
        // (marginLeft = borde izq del árbol) para dejar los catálogos visibles a la
        // izquierda. Tope de alto 55% para no comerse el árbol.
        position: 'relative', flexShrink: 0, height: size, maxHeight: '55%', minHeight: 0,
        ...(treeWidth > 0 ? { alignSelf: 'flex-start', marginLeft: treeLeft, width: treeWidth } : { alignSelf: 'stretch' }),
        background: 'var(--bg-surface)', borderTop: '1px solid var(--border-subtle)',
        boxShadow: '0 -12px 30px -14px rgba(16,24,40,0.22)',
        display: 'flex', flexDirection: 'column',
      }
    : {
        // Tope de ancho (50%) para que el grid nunca quede aplastado por el panel.
        position: 'relative', flexShrink: 0, width: size, maxWidth: '50%', alignSelf: 'stretch', minHeight: 0,
        background: 'var(--bg-surface)', borderLeft: '1px solid var(--border-subtle)',
        boxShadow: '-12px 0 30px -14px rgba(16,24,40,0.22)',
        display: 'flex', flexDirection: 'column',
      };

  // Arrastre del borde para redimensionar (borde izquierdo en 'derecha', borde
  // superior en 'abajo'). Persistencia la hace el contenedor vía onResize.
  function startResize(e: React.MouseEvent) {
    e.preventDefault();
    const onMove = (ev: MouseEvent) => {
      if (mode === 'abajo') {
        onResize(Math.min(window.innerHeight * 0.8, Math.max(200, window.innerHeight - ev.clientY)));
      } else {
        onResize(Math.min(760, Math.max(340, window.innerWidth - ev.clientX)));
      }
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }
  const resizeHandle: React.CSSProperties = mode === 'abajo'
    ? { position: 'absolute', top: -3, left: 0, right: 0, height: 7, cursor: 'ns-resize', zIndex: 1 }
    : { position: 'absolute', top: 0, bottom: 0, left: -3, width: 7, cursor: 'ew-resize', zIndex: 1 };
  const sectionLabel: React.CSSProperties = {
    fontSize: 10.5, letterSpacing: '0.05em', textTransform: 'uppercase',
    color: 'var(--text-tertiary)', fontWeight: 700, margin: '18px 0 6px',
  };
  const factRow: React.CSSProperties = {
    display: 'flex', justifyContent: 'space-between', gap: 14, fontSize: 13,
    padding: '7px 0', borderBottom: '1px dashed var(--border-subtle)',
  };
  const pillCatalogo: React.CSSProperties = {
    display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600,
    color: 'var(--text-secondary)', background: 'var(--bg-canvas)',
    border: '1px solid var(--border-subtle)', borderRadius: 999, padding: '2px 9px',
  };
  const modalLabel: React.CSSProperties = {
    fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em',
    color: tokens.colorNeutralForeground3, fontWeight: 700, margin: '16px 0 4px',
  };

  const cuad = cuadrillaComps(m?.cuadrilla);

  // Modo ANCHO (panel abajo): en vez de una columna angosta con filas
  // "etiqueta … valor" (que dejan un hueco enorme), se reparte en tiles + 2
  // columnas para aprovechar el ancho. En "derecha" (angosto) se apila igual.
  const wide = mode === 'abajo';
  // Tile compacto para los números (Cantidad / P.U. / Importe).
  const numTile = (label: string, value: React.ReactNode, opts: { mono?: boolean; strong?: boolean } = {}) => (
    <div style={{
      // Con el panel angosto los tiles se REPARTEN el ancho (crecen y, si no caben,
      // el que baja de renglón lo llena) para que no queden huecos raros. En modo
      // "abajo" (ancho) siguen a su tamaño natural: estirar 3 tiles a toda la fila
      // se vería desproporcionado.
      flex: wide ? '0 0 auto' : '1 1 128px', minWidth: 128, background: 'var(--bg-canvas)',
      border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '8px 12px',
    }}>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-tertiary)', fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 15, fontWeight: opts.strong ? 800 : 700, marginTop: 3, color: 'var(--text-primary)', fontVariantNumeric: 'tabular-nums', fontFamily: opts.mono ? 'var(--font-mono)' : undefined }}>{value}</div>
    </div>
  );

  // PROCEDIMIENTO CONSTRUCTIVO — la lámina técnica imprimible del concepto (staff
  // Ecosoft, solo conceptos con análisis de IA). Es una funcionalidad estrella, así
  // que va RESALTADA y justo debajo de "Ver memoria de cálculo" (Sebastián,
  // 2026-09-21). Se define una sola vez y se renderiza en un único punto según el
  // caso (precio activo = IA → junto a la memoria; precio activo = catálogo pero con
  // análisis de IA → tras el bloque de origen), para no perder el botón en ningún caso.
  const botonProcedimiento = tieneAnalisisIa ? (
    <button
      type="button"
      className={novProc.visible ? novStyles.halo : undefined}
      onClick={() => { novProc.marcarVisto(); setProcedimientoOpen(true); }}
      onMouseEnter={() => setHoverProc(true)}
      onMouseLeave={() => setHoverProc(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left',
        marginTop: 12, padding: '9px 12px', borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit',
        border: '1px solid var(--border-subtle)', borderLeft: '3px solid var(--accent)',
        background: 'var(--accent-weak)',
        boxShadow: hoverProc ? '0 4px 14px rgba(0,0,0,0.16)' : 'none',
        transform: hoverProc ? 'translateY(-1px)' : undefined,
        transition: 'transform 0.12s ease, box-shadow 0.12s ease',
      }}
    >
      <span
        style={{
          flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 32, height: 32, borderRadius: 9, background: 'var(--accent)', color: '#fff',
        }}
      >
        <DocumentBulletList20Regular />
      </span>
      <span style={{ minWidth: 0, flex: 1 }}>
        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 750, color: 'var(--text-primary)' }}>
          Procedimiento constructivo
          {novProc.visible && <span className={novStyles.badge}>novedad</span>}
        </span>
        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-secondary)', marginTop: 1 }}>
          Lámina técnica del concepto, lista para el residente
        </span>
      </span>
      <span
        style={{
          flexShrink: 0, fontSize: 16, fontWeight: 700, color: 'var(--accent)',
          opacity: hoverProc ? 1 : 0.75,
          transform: hoverProc ? 'translateX(2px)' : undefined,
          transition: 'transform 0.12s ease, opacity 0.12s ease',
        }}
      >
        →
      </span>
    </button>
  ) : null;

  return (
    <aside style={drawer} role="complementary" aria-label="Análisis de precio del concepto">
      {/* Asa de redimensionado (borde interior hacia el grid) */}
      <div style={resizeHandle} onMouseDown={startResize} aria-hidden />
      {/* Header — los controles (posición del panel + cerrar) van en su PROPIA fila,
          pegados al borde superior, y el título/descripción abajo a TODO el ancho.
          Antes compartían fila: los botones le comían ~140 px al texto y la
          descripción empezaba a envolver antes de tiempo. */}
      <div style={{ padding: '6px 10px 14px 18px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 4 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, flexShrink: 0 }}>
          {/* RECORRER LOS RENGLONES QUE SE ESTÁN VIENDO. Recorre la lista del filtro
              aplicado: si filtraste "Por revisar", las flechas van de uno al siguiente de
              esos. Se apagan cuando hay cambios sin guardar (pasar de renglón se llevaría
              lo escrito sin avisar). */}
          {nav && nav.total > 1 && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 2, marginRight: 'auto' }}>
              <Tooltip
                relationship="label"
                content={cambios.hayCambios
                  ? 'Guarda o descarta los cambios para pasar a otro renglón'
                  : 'Renglón anterior (flecha izquierda)'}
              >
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<ChevronLeft20Regular />}
                  aria-label="Renglón anterior"
                  disabled={cambios.hayCambios || nav.pos <= 1}
                  onClick={nav.onPrev}
                />
              </Tooltip>
              <span style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums', minWidth: 62, textAlign: 'center' }}>
                {nav.pos} de {nav.total}
              </span>
              <Tooltip
                relationship="label"
                content={cambios.hayCambios
                  ? 'Guarda o descarta los cambios para pasar a otro renglón'
                  : 'Renglón siguiente (flecha derecha)'}
              >
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<ChevronRight20Regular />}
                  aria-label="Renglón siguiente"
                  disabled={cambios.hayCambios || nav.pos >= nav.total}
                  onClick={nav.onNext}
                />
              </Tooltip>
            </div>
          )}
          {/* Cambiar posición del panel (derecha / abajo) — como i2 */}
          {onModeChange && (
            <div style={{ display: 'inline-flex', gap: 2, flexShrink: 0, background: 'var(--bg-canvas)', border: '1px solid var(--border-subtle)', borderRadius: 8, padding: 2 }}>
              {([
                { m: 'derecha' as const, Icon: PanelRight20Regular, label: 'Ver el análisis a la derecha' },
                { m: 'abajo' as const, Icon: PanelBottom20Regular, label: 'Ver el análisis abajo' },
              ]).map(({ m, Icon, label }) => {
                const on = mode === m;
                return (
                  <button
                    key={m}
                    type="button"
                    title={label}
                    aria-label={label}
                    aria-pressed={on}
                    onClick={() => onModeChange(m)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 30, height: 26, borderRadius: 6, border: 'none', cursor: 'pointer',
                      background: on ? 'var(--bg-surface)' : 'transparent',
                      color: on ? 'var(--accent)' : 'var(--text-secondary)',
                      boxShadow: on ? '0 1px 3px rgba(16,24,40,0.18)' : 'none',
                    }}
                  >
                    <Icon />
                  </button>
                );
              })}
            </div>
          )}
          <Button appearance="subtle" icon={<Dismiss20Regular />} aria-label="Cerrar" onClick={onClose} style={{ flexShrink: 0 }} />
        </div>
        {/* EL RENGLÓN, EDITABLE EN EL LUGAR. Ancho completo del panel (nada a su derecha).
            Sin botón "Editar" previo: a estos renglones se les acaba de pedir que se
            corrijan, así que un clic de más es un paso para nada. Guardar y Descartar
            aparecen recién cuando algo cambió. */}
        <div style={{ minWidth: 0, paddingRight: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-tertiary)' }}>
              {concepto.clave || '—'}
            </span>
            {concepto.editadoManual && (
              <Tooltip
                relationship="description"
                content={
                  <div style={{ fontSize: 12, lineHeight: 1.5, maxWidth: 300 }}>
                    {concepto.editadoPorNombre
                      ? `Lo corrigió ${concepto.editadoPorNombre}${cuando(concepto.editadoAt) ? ` el ${cuando(concepto.editadoAt)}` : ''}.`
                      : 'Este renglón se corrigió a mano.'}
                  </div>
                }
              >
                <span style={{ display: 'inline-flex', cursor: 'help' }}>
                  <Badge size="small" appearance="tint" color="brand">Corregido a mano</Badge>
                </span>
              </Tooltip>
            )}
          </div>

          {/* El renglón que no se pudo leer: acá se dice qué hay que hacer con él, justo
              arriba de los campos donde se hace. */}
          {concepto.origen === 'conteo_pagina' && !concepto.editadoManual && (
            <div style={{
              display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, lineHeight: 1.45,
              color: 'var(--text-secondary)', background: 'var(--warn-bg, rgba(180,83,9,0.10))',
              border: '1px solid var(--warn)', borderRadius: 8, padding: '8px 10px',
            }}>
              <Warning20Regular style={{ flexShrink: 0, color: 'var(--warn)' }} />
              <span>
                Este renglón no se pudo leer completo
                {concepto.origenPagina != null ? ` de la página ${concepto.origenPagina}` : ''} del documento:
                la descripción quedó cortada y la cantidad puede no ser la que dice el original.
                Compáralo con el documento y corrígelo aquí abajo.
              </span>
            </div>
          )}

          <Textarea
            value={borrador.descripcion}
            onChange={(_, d) => setBorrador((b) => ({ ...b, descripcion: d.value }))}
            disabled={!canEdit || guardando}
            resize="vertical"
            aria-label="Descripción del renglón"
            // La MISMA tipografía que tenía la descripción cuando era solo texto (14 / 650 /
            // 1.35): al volverse editable había quedado más chica y más liviana, y el renglón
            // perdió su jerarquía en el panel — es el título de lo que estás mirando.
            textarea={{
              style: {
                minHeight: 62, maxHeight: 220,
                fontSize: 14, fontWeight: 650, lineHeight: 1.35, color: 'var(--text-primary)',
              },
            }}
            style={{ width: '100%' }}
          />

          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-tertiary)', fontWeight: 700 }}>
                Unidad
              </span>
              <Input
                value={borrador.unidad}
                onChange={(_, d) => setBorrador((b) => ({ ...b, unidad: d.value }))}
                disabled={!canEdit || guardando}
                size="small"
                aria-label="Unidad del renglón"
                style={{ width: 96 }}
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--text-tertiary)', fontWeight: 700 }}>
                Cantidad
              </span>
              <Input
                value={borrador.cantidad}
                onChange={(_, d) => setBorrador((b) => ({ ...b, cantidad: d.value }))}
                disabled={!canEdit || guardando}
                size="small"
                inputMode="decimal"
                aria-label="Cantidad del renglón"
                style={{ width: 132 }}
                input={{ style: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } }}
              />
            </label>
            {cambios.hayCambios && (
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginLeft: 'auto' }}>
                <Button appearance="secondary" size="small" onClick={descartar} disabled={guardando}>
                  Descartar
                </Button>
                <Button appearance="primary" size="small" onClick={() => void guardar()} disabled={!puedeGuardar}>
                  {guardando
                    ? 'Guardando…'
                    : seVaABuscar ? 'Guardar y buscar el precio' : 'Guardar'}
                </Button>
              </div>
            )}
          </div>

          {/* EL BOTÓN DICE LA CONSECUENCIA, y acá se explica en una frase. Nada de
              carteles de advertencia aparte. */}
          {cambios.hayCambios && (
            <div style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--text-secondary)' }}>
              {cantidadNum == null
                ? 'La cantidad tiene que ser un número.'
                : seVaABuscar
                  ? 'Al guardar se vuelve a buscar el precio de este renglón en tus catálogos: la descripción y la unidad son las que deciden con qué concepto se compara.'
                  : 'Al guardar cambia el importe de este renglón y el total del presupuesto.'}
            </div>
          )}
          {/* Un análisis hecho con inteligencia artificial no se reemplaza solo. */}
          {cambios.hayCambios && (isSynthetic || concepto.hasApuIaMatrix) && (
            <div style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--text-secondary)' }}>
              El análisis de precio de este renglón no se toca. Si quieres que refleje lo que
              acabas de corregir, vuelve a crearlo con inteligencia artificial desde el botón de abajo.
            </div>
          )}
          {errorEdicion && (
            <div style={{ fontSize: 12, color: 'var(--bad)' }}>{errorEdicion}</div>
          )}
          {!canEdit && (
            <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>
              {/* Sin "ahora": los dos casos que llegan acá —el presupuesto de ejemplo y un
                  usuario que solo puede mirar— no cambian por esperar. El presupuesto que
                  se está armando no llega: ahí el panel no abre. */}
              Este renglón no se puede corregir.
            </div>
          )}
        </div>
      </div>

      {/* Body — UN solo scroll (antes en 'abajo' cada columna scrolleaba por
          separado y confundía; ahora scrollea el panel entero como uno). */}
      <div style={{ padding: '4px 18px 18px', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        {/* Datos rápidos como tiles compactos (evita filas etiqueta…valor con hueco) */}
        {/* La cantidad ya no vive acá: se escribe arriba, junto a la descripción y la
            unidad. Tener el mismo número en un campo editable y en un tile de solo
            lectura hacía dudar de cuál manda. */}
        <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap', flexShrink: 0 }}>
          {numTile('Precio unitario', money(concepto.precioUnitario), { mono: true })}
          {numTile('Importe', money(concepto.importe), { mono: true, strong: true })}
          {/* EL OTRO PRECIO, A LA VISTA. El concepto tiene los dos guardados y solo uno
              cuenta: si el que no cuenta queda escondido, reemplazarlo pasa desapercibido
              (fue exactamente lo que le pasó a un tester: perdió el precio de su catálogo
              con coincidencia perfecta y nada en pantalla lo decía). */}
          {hayQueElegir && otroPrecio != null && numTile(
            isSynthetic ? 'El de tu catálogo' : 'El armado con IA',
            <span style={{ color: 'var(--text-secondary)' }}>{money(otroPrecio)}</span>,
            { mono: true },
          )}
        </div>

        {/* Split: izquierda = origen + acciones; derecha = análisis. En ancho van
            lado a lado (cada columna con su scroll); en angosto se apilan. */}
        <div style={{ display: 'flex', flexDirection: wide ? 'row' : 'column', gap: wide ? 24 : 0, alignItems: wide ? 'flex-start' : 'stretch', marginTop: wide ? 12 : 0 }}>
          <div style={{ flex: wide ? '0 0 340px' : '0 0 auto', minWidth: 0, ...(wide ? { paddingRight: 8 } : {}) }}>
            {/* Origen del precio */}
            <div style={{ ...sectionLabel, marginTop: wide ? 0 : 18 }}>Origen del precio</div>
        {isSynthetic ? (
          <>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 600, padding: '7px 11px', borderRadius: 8, color: 'var(--privado)', background: 'var(--privado-bg, rgba(124,58,237,0.10))' }}>
              ✦ Precio construido con inteligencia artificial
            </div>

            {/* Cómo lo pensó (estrella) + memoria de cálculo → modal in-app, justo tras el pill */}
            {(tienePensado || tieneMemoria) && (
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginTop: 12 }}>
                {tienePensado && (
                  <button
                    type="button"
                    data-demo="como-penso"
                    onClick={() => setPensadoOpen(true)}
                    onMouseEnter={() => setHoverPensado(true)}
                    onMouseLeave={() => setHoverPensado(false)}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '9px 18px', borderRadius: 999, border: 'none', cursor: 'pointer',
                      fontSize: 13, fontWeight: 700, color: '#fff', fontFamily: 'inherit', whiteSpace: 'nowrap',
                      background: 'linear-gradient(120deg, var(--accent), var(--privado))',
                      boxShadow: hoverPensado ? '0 6px 20px rgba(108,84,214,0.55)' : '0 2px 12px rgba(108,84,214,0.35)',
                      transform: hoverPensado ? 'translateY(-1px)' : undefined,
                      filter: hoverPensado ? 'brightness(1.06)' : undefined,
                      transition: 'transform 0.12s ease, box-shadow 0.12s ease, filter 0.12s ease',
                    }}
                  >
                    <span style={{ fontSize: 15 }}>🧠</span>
                    Cómo lo pensó {nombreProductoActivo()}
                    <span style={{ fontSize: 13, filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.65))' }}>✨</span>
                  </button>
                )}
                {tieneMemoria && (
                  <Button appearance="outline" size="small" icon={<Document20Regular />} onClick={() => setMemoriaOpen(true)}>
                    Ver memoria de cálculo
                  </Button>
                )}
              </div>
            )}
            {/* Procedimiento constructivo — resaltado, justo debajo de la memoria. */}
            {botonProcedimiento}
            {loadingApu && (
              <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--text-tertiary)' }}>
                <Spinner size="extra-tiny" /> Cargando análisis…
              </div>
            )}
            {/* El aviso de "revisa este precio" con el motivo de cobertura se QUITÓ del panel
                del cliente (Sebastián, 2026-08-14: "nunca te lo pedí"). El semáforo y su motivo
                siguen calculándose y guardándose (`apu_ia_confianza_semaforo/_razon`) y se ven en
                la auditoría de precios de staff. Para reponerlo, este bloque leía
                `apu.apu_ia_confianza_razon` y pintaba título+color según el semáforo. */}
            {factores.length > 0 && (
              <>
                <div style={{ ...sectionLabel, margin: '14px 0 6px' }}>Contexto de proyecto aplicado</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
                  {factores.map((f) => {
                    const label = contextoLabel(f);
                    // Texto COMPLETO que envuelve (sin cortar ni depender de tooltip:
                    // en iPad no hay hover — se veía truncado). Ver
                    // feedback_explicaciones_para_todos.
                    return (
                      <span
                        key={f.id}
                        style={{
                          fontSize: 11.5, lineHeight: 1.35, background: 'var(--bg-canvas)',
                          color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)',
                          borderRadius: 8, padding: '4px 9px', whiteSpace: 'normal', maxWidth: '100%',
                        }}
                      >
                        {label}
                      </span>
                    );
                  })}
                </div>
              </>
            )}
          </>
        ) : hasMatch ? (
          <>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 600, padding: '7px 11px', borderRadius: 8, color: 'var(--ok)', background: 'var(--ok-bg, rgba(5,150,105,0.10))' }}>
              ● Equivale a un concepto de tu catálogo
            </div>
            <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 5 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {catalogName && (
                  <span style={pillCatalogo}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--ok)' }} />
                    {catalogName}
                  </span>
                )}
                {concepto.claveCatalogo && (
                  <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 12, color: tokens.colorBrandForeground1 }}>{concepto.claveCatalogo}</span>
                )}
              </div>
              {concepto.descripcionCatalogo && (
                <span style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.4 }}>{concepto.descripcionCatalogo}</span>
              )}
              {concepto.unidadCatalogo && (
                <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>Unidad: {concepto.unidadCatalogo}</span>
              )}
            </div>
            {concepto.score != null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12 }}>
                <span style={{ fontSize: 10, color: 'var(--text-tertiary)' }}>Qué tan parecido</span>
                <div style={{ position: 'relative', flex: 1, height: 5, borderRadius: 999, background: 'linear-gradient(to right, var(--bad) 0%, var(--warn) 40%, var(--ok) 70%, var(--ok) 100%)' }}>
                  <div style={{ position: 'absolute', left: `${Math.round(concepto.score * 100)}%`, top: '50%', transform: 'translate(-50%, -50%)', width: 9, height: 9, borderRadius: '50%', background: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.4)', border: '1.5px solid rgba(0,0,0,0.15)' }} />
                </div>
                <span style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums', fontWeight: 700, color: 'var(--text-secondary)' }}>{Math.round(concepto.score * 100)}%</span>
              </div>
            )}
            {llmVeredicto && (
              <div style={{ marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600, color: llmVeredicto === 'si' ? 'var(--ok)' : 'var(--warn)' }}>
                {llmVeredicto === 'si'
                  ? <><CheckmarkCircle20Filled style={{ fontSize: 14 }} /> Equivalencia verificada</>
                  : <><Warning20Regular style={{ fontSize: 14 }} /> Revisa la equivalencia</>}
              </div>
            )}
            {concepto.nota && (
              <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '10px 0 0', fontStyle: 'italic' }}>
                “{concepto.nota.replace(/^(LLM|IA):\s*/i, '')}”
              </p>
            )}
          </>
        ) : (
          (() => {
            const { porque, pasos } = sinEquivalente(concepto);
            return (
              <>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12.5, fontWeight: 600, padding: '7px 11px', borderRadius: 8, color: 'var(--warn)', background: 'var(--warn-bg, rgba(180,83,9,0.10))' }}>
                  Todavía sin precio
                </div>
                <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: '10px 0 0', lineHeight: 1.5 }}>{porque}</p>
                <div style={{ ...sectionLabel, margin: '14px 0 6px' }}>Qué puedes hacer</div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.55, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {pasos.map((p, i) => (
                    <li key={i}>
                      {p.irABuscar ? (
                        <button
                          type="button"
                          onClick={onGoToEquivalentes}
                          title="Ir al paso “Buscar equivalentes”"
                          style={{
                            display: 'inline', textAlign: 'left', background: 'none', border: 'none', padding: 0,
                            margin: 0, cursor: 'pointer', font: 'inherit', color: 'var(--accent)', lineHeight: 1.55,
                          }}
                        >
                          {p.texto}
                          {' '}
                          <span aria-hidden style={{ fontWeight: 700 }}>↑</span>
                        </button>
                      ) : p.texto}
                    </li>
                  ))}
                </ul>
              </>
            );
          })()
        )}

        {/* ===== ELEGIR CUÁL DE LOS DOS PRECIOS VALE =====
            Solo aparece cuando el concepto tiene los dos guardados: el que se encontró en
            los catálogos y el que armó la inteligencia artificial. Cambiar es inmediato y no
            cuesta nada. Es la puerta que faltaba: el mecanismo estaba en el servidor desde
            la migración 065 y ningún botón lo llamaba. */}
        {hayQueElegir && (
          <>
            <div style={sectionLabel}>Qué precio usar en el presupuesto</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {([
                {
                  source: 'ia' as const,
                  activa: isSynthetic,
                  titulo: 'El armado con inteligencia artificial',
                  precio: isSynthetic ? concepto.precioUnitario : (concepto.precioIa ?? null),
                  detalle: null as string | null,
                  color: 'var(--privado)',
                },
                {
                  source: 'catalog' as const,
                  activa: !isSynthetic,
                  titulo: 'El de tu catálogo',
                  precio: isSynthetic ? (concepto.precioCatalogoGuardado ?? null) : concepto.precioUnitario,
                  detalle: [
                    concepto.claveCatalogo,
                    (() => {
                      const s = concepto.scoreCatalogoGuardado ?? concepto.score;
                      return s != null ? `se parece ${Math.round(s * 100)}%` : null;
                    })(),
                  ].filter(Boolean).join(' · ') || null,
                  color: 'var(--ok)',
                },
              ]).map((op) => (
                <button
                  key={op.source}
                  type="button"
                  disabled={op.activa || cambiandoFuente || isActive || op.precio == null}
                  onClick={() => void elegirFuente(op.source)}
                  aria-pressed={op.activa}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, textAlign: 'left', width: '100%',
                    font: 'inherit', padding: '10px 12px', borderRadius: 10,
                    border: `1px solid ${op.activa ? op.color : 'var(--border-subtle)'}`,
                    background: op.activa ? 'var(--bg-canvas)' : 'transparent',
                    cursor: op.activa || cambiandoFuente || isActive ? 'default' : 'pointer',
                    opacity: op.precio == null ? 0.5 : 1,
                  }}
                >
                  <span style={{
                    flexShrink: 0, width: 14, height: 14, borderRadius: '50%',
                    border: `2px solid ${op.activa ? op.color : 'var(--border-strong)'}`,
                    background: op.activa
                      ? `radial-gradient(circle, ${op.color} 0 45%, transparent 46%)`
                      : 'transparent',
                  }} />
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: 'block', fontSize: 12.5, fontWeight: 650, color: 'var(--text-primary)' }}>
                      {op.titulo}
                    </span>
                    {op.detalle && (
                      <span style={{ display: 'block', fontSize: 11, color: 'var(--text-tertiary)', marginTop: 1 }}>
                        {op.detalle}
                      </span>
                    )}
                  </span>
                  <span style={{ flexShrink: 0, textAlign: 'right' }}>
                    <span style={{
                      display: 'block', fontFamily: 'var(--font-mono)', fontSize: 13, fontWeight: 800,
                      fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)',
                    }}>
                      {money(op.precio)}
                    </span>
                    <span style={{ display: 'block', fontSize: 10.5, color: op.activa ? op.color : 'var(--accent)', fontWeight: 700, marginTop: 1 }}>
                      {op.activa ? 'EN USO' : cambiandoFuente ? 'Cambiando…' : 'Usar este'}
                    </span>
                  </span>
                </button>
              ))}
            </div>
            <p style={{ fontSize: 11.5, color: 'var(--text-secondary)', margin: '8px 0 0', lineHeight: 1.45 }}>
              El que elijas es el que entra al total del presupuesto. Cambiar es inmediato, no
              gasta nada y puedes volver cuando quieras.
              {/* La comparación es un DATO de los dos números que ya están en pantalla, no un
                  criterio: acá no se decide nada, solo se dice en cuánto se diferencian. */}
              {vecesMasCaro != null && vecesMasCaro > 1 && (
                <> El armado con inteligencia artificial es <b>{num(vecesMasCaro, 1)} veces</b> el de tu catálogo.</>
              )}
              {vecesMasCaro != null && vecesMasCaro < 1 && (
                <> El de tu catálogo es <b>{num(1 / vecesMasCaro, 1)} veces</b> el armado con inteligencia artificial.</>
              )}
            </p>
          </>
        )}

        {/* Procedimiento constructivo cuando hay análisis de IA pero el precio activo
            es el del catálogo (en ese caso no se mostró junto a la memoria). */}
        {!isSynthetic && botonProcedimiento}

        {/* Insumos auxiliares sin composición → pedir a la IA que la arme */}
        {isSynthetic && auxiliaresSinResolver.length > 0 && (
          <>
            <div style={sectionLabel}>Insumos por definir</div>
            <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '0 0 8px', lineHeight: 1.45 }}>
              La inteligencia artificial usó estos insumos pero no armó su composición. Pídele que la arme para completar el precio.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {auxiliaresSinResolver.map(({ mat, i }) => (
                <div key={i} style={{ border: '1px solid var(--border-subtle)', borderRadius: 8, padding: '10px 12px', background: 'var(--bg-canvas)' }}>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-primary)' }}>{mat.descripcion_normalizada}</div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 2 }}>Unidad: {mat.unidad}</div>
                  <Button
                    appearance="primary"
                    size="small"
                    disabled={isActive || resolvingAux !== null}
                    onClick={() => void resolverAux(i, mat.descripcion_normalizada, mat.unidad)}
                    style={{ marginTop: 8 }}
                  >
                    {resolvingAux === i ? '⏳ Consultando…' : '🪄 Pedir a la inteligencia artificial que arme la composición'}
                  </Button>
                </div>
              ))}
            </div>
          </>
        )}
          </div>{/* /columna izquierda (origen + acciones) */}

          <div style={{ flex: wide ? '1 1 0' : '0 0 auto', minWidth: 0 }}>
            {/* Análisis de precio unitario (desglose actual) — solo si hay algo que mostrar */}
            {(isSynthetic || (concepto.insumos?.length ?? 0) > 0) ? (
              <>
                <div style={{ ...sectionLabel, marginTop: wide ? 0 : 18 }}>Análisis de precio unitario</div>
                <ConceptAnalysisView quoteId={quoteId} concepto={concepto} preloadedApu={apu} moneda={moneda} />
              </>
            ) : wide ? (
              <div style={{ fontSize: 12, color: 'var(--text-tertiary)', paddingTop: 8 }}>Este concepto no tiene desglose de precio.</div>
            ) : null}
          </div>{/* /columna derecha (análisis) */}
        </div>{/* /split */}
      </div>

      {/* Footer de acciones (sintéticos) */}
      {isSynthetic && (
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--border-subtle)', display: 'flex', flexDirection: 'column', gap: 8, background: 'var(--bg-canvas)' }}>
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center' }}>
            {reviewStatus === 'approved' ? (
              <>
                <Badge appearance="tint" color="success" style={{ alignSelf: 'center' }}>Matriz aceptada</Badge>
                <Button appearance="secondary" disabled={isActive || busy} onClick={() => run(() => reopenMatrix(concepto.id))} size="small" style={{ flex: '0 0 auto' }}>Reabrir</Button>
              </>
            ) : reviewStatus === 'rejected' ? (
              <>
                <Badge appearance="tint" color="danger" style={{ alignSelf: 'center' }}>Matriz rechazada</Badge>
                <Button appearance="secondary" disabled={isActive || busy} onClick={() => run(() => reopenMatrix(concepto.id))} size="small" style={{ flex: '0 0 auto' }}>Reabrir</Button>
              </>
            ) : (
              <>
                <Button
                  appearance="secondary"
                  disabled={isActive || busy}
                  onClick={() => void onReject()}
                  size="small"
                  style={{ flex: '0 0 auto' }}
                  title={puedeVolverAlCatalogo(concepto)
                    ? 'Descarta este análisis y deja activo otra vez el precio de tu catálogo.'
                    : 'Descarta este análisis. Este concepto no tiene precio de catálogo al que volver.'}
                >
                  {puedeVolverAlCatalogo(concepto) ? 'Rechazar y volver al de mi catálogo' : 'Rechazar matriz'}
                </Button>
                <Button appearance="primary" disabled={isActive || busy} onClick={() => run(() => approveMatrix(concepto.id))} size="small" style={{ flex: '0 0 auto' }}>Aceptar matriz</Button>
              </>
            )}
          </div>
          {reviewStatus !== 'approved' && reviewStatus !== 'rejected' && (
            <Button
              appearance="subtle"
              size="small"
              disabled={isActive || busy}
              onClick={() => void onRegenerate()}
              style={{ color: 'var(--privado)' }}
              title="Vuelve a crear este análisis con inteligencia artificial (cuenta como 1 análisis generado en tu cuota)."
            >
              ↻ Volver a crear con inteligencia artificial
            </Button>
          )}
        </div>
      )}

      {/* ===== Modal: Cómo lo pensó OPUS ai (razonamiento + memoria de decisiones + validaciones/alertas) ===== */}
      <Dialog open={pensadoOpen} onOpenChange={(_, d) => { if (!d.open) setPensadoOpen(false); }}>
        <DialogSurface style={{ maxWidth: 720, width: '92vw' }}>
          <DialogBody>
            <DialogTitle>🧠 Cómo lo pensó {nombreProductoActivo()}</DialogTitle>
            <DialogContent style={{ fontSize: 13, lineHeight: 1.6 }}>
              <p style={{ marginTop: 0, color: 'var(--text-secondary)' }}>
                Así razonó la inteligencia artificial para construir este precio. Es el criterio detrás del análisis; los parámetros internos de calibración de Ecosoft no se muestran.
              </p>
              {razonamientos.map((r) => (
                <div key={r.k}>
                  <div style={modalLabel}>{r.k}</div>
                  <div style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{r.v}</div>
                </div>
              ))}
              {/* Caso real de que no haya explicación guardada. Se dice tal cual:
                  antes acá vivía una frase que prometía el detalle "bajo petición",
                  y no había dónde pedirlo. */}
              {razonamientos.length === 0 && !m?.memoria_decisiones && (
                <p style={{ color: 'var(--text-secondary)' }}>
                  De este análisis no quedó guardada la explicación. El precio y su desglose sí están
                  completos; para tener la explicación, vuelve a crear el análisis con inteligencia artificial.
                </p>
              )}
              {m?.memoria_decisiones && (
                <>
                  <div style={modalLabel}>Memoria de decisiones</div>
                  <div style={{ color: 'var(--text-secondary)', whiteSpace: 'pre-wrap' }}>{m.memoria_decisiones}</div>
                </>
              )}
              {(validaciones.length > 0 || alertas.length > 0 || preguntas.length > 0) && (
                <>
                  <div style={modalLabel}>Validaciones y alertas</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {validaciones.map((v) => (
                      <div key={v.id} style={{ color: v.tipo === 'success' ? 'var(--ok)' : v.tipo === 'warning' ? 'var(--warn)' : 'var(--bad)' }}>
                        {v.tipo === 'success' ? '✓' : v.tipo === 'warning' ? '⚠' : '✕'} {v.msg}
                      </div>
                    ))}
                    {alertas.map((a, i) => (
                      <div key={`a${i}`} style={{ color: 'var(--warn)' }}>⚠ {a}</div>
                    ))}
                    {preguntas.map((p, i) => (
                      <div key={`p${i}`} style={{ color: 'var(--text-secondary)' }}>· {p}</div>
                    ))}
                  </div>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setPensadoOpen(false)}>Cerrar</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* ===== Modal: Ver memoria de cálculo (composición del PU, client-safe: sin % de factores) ===== */}
      <Dialog open={memoriaOpen} onOpenChange={(_, d) => { if (!d.open) setMemoriaOpen(false); }}>
        <DialogSurface style={{ maxWidth: 680, width: '92vw' }}>
          <DialogBody>
            <DialogTitle>Memoria de cálculo</DialogTitle>
            <DialogContent style={{ fontSize: 13, lineHeight: 1.6 }}>
              <div style={{ color: 'var(--text-tertiary)', fontSize: 12, marginBottom: 4 }}>
                {[concepto.clave, concepto.descripcion].filter(Boolean).join(' · ')}
              </div>

              <div style={modalLabel}>Rendimiento</div>
              <div style={factRow}><span style={{ color: 'var(--text-secondary)' }}>Rendimiento base</span><b>{num(apu?.apu_ia_rendimiento_base ?? m?.rendimiento_base)} {m?.rendimiento_base_unidad ?? concepto.unidad}</b></div>
              {apu?.apu_ia_rendimiento_ajustado != null && (
                <div style={factRow}><span style={{ color: 'var(--text-secondary)' }}>Rendimiento ajustado al contexto</span><b>{num(apu.apu_ia_rendimiento_ajustado)} {m?.rendimiento_base_unidad ?? concepto.unidad}</b></div>
              )}

              <div style={modalLabel}>Mano de obra</div>
              <div style={{ color: 'var(--text-secondary)', marginBottom: 6 }}>
                {cuad.length ? cuad.map((c) => `${num(c.cantidad, 2)} × ${c.categoria.replace(/_/g, ' ')}`).join(' + ') : 'Cuadrilla'}
              </div>
              {apu?.apu_ia_sr_cuadrilla != null && (
                <div style={factRow}><span style={{ color: 'var(--text-secondary)' }}>Salario real / jornada</span><b>{money(apu.apu_ia_sr_cuadrilla)}</b></div>
              )}

              <div style={modalLabel}>Composición del precio unitario</div>
              {apu?.apu_ia_sub_materiales != null && <div style={factRow}><span style={{ color: 'var(--text-secondary)' }}>Materiales</span><b>{money(apu.apu_ia_sub_materiales)}</b></div>}
              {apu?.apu_ia_sub_mo != null && <div style={factRow}><span style={{ color: 'var(--text-secondary)' }}>Mano de obra</span><b>{money(apu.apu_ia_sub_mo)}</b></div>}
              {apu?.apu_ia_sub_equipo != null && <div style={factRow}><span style={{ color: 'var(--text-secondary)' }}>Equipo</span><b>{money(apu.apu_ia_sub_equipo)}</b></div>}
              {apu?.apu_ia_sub_herramienta != null && <div style={factRow}><span style={{ color: 'var(--text-secondary)' }}>Herramienta menor</span><b>{money(apu.apu_ia_sub_herramienta)}</b></div>}
              {apu?.apu_ia_costo_directo != null && (
                <div style={{ ...factRow, borderBottom: 'none', borderTop: '2px solid var(--border-strong)', marginTop: 4, paddingTop: 8, fontWeight: 800 }}>
                  <span>Precio unitario · costo directo</span><b>{money(apu.apu_ia_costo_directo)}</b>
                </div>
              )}

              {factores.length > 0 && (
                <>
                  <div style={modalLabel}>Rendimiento y factores aplicados</div>
                  {factores.map((f) => (
                    <div key={f.id} style={{ paddingLeft: 4, marginBottom: 8 }}>
                      {isStaff ? (
                        // Solo staff Ecosoft ve el parámetro de calibración (% + fuente + id).
                        <>
                          <code style={{ background: tokens.colorNeutralBackground2, padding: '1px 6px', borderRadius: 3 }}>{f.id}</code>{' '}
                          <strong style={{ color: f.ajuste_pct < 0 ? 'var(--bad)' : 'var(--ok)' }}>
                            {f.ajuste_pct > 0 ? '+' : ''}{f.ajuste_pct}%
                          </strong>
                          {f.fuente && (
                            <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-tertiary)', fontStyle: 'italic' }}>📖 {f.fuente}</span>
                          )}
                        </>
                      ) : (
                        // Cliente: solo el efecto cualitativo, nunca el porcentaje.
                        <strong style={{ color: f.ajuste_pct < 0 ? 'var(--bad)' : f.ajuste_pct > 0 ? 'var(--ok)' : 'var(--text-secondary)' }}>
                          {f.ajuste_pct < 0 ? 'Menor rendimiento' : f.ajuste_pct > 0 ? 'Mayor rendimiento' : 'Sin efecto en rendimiento'}
                        </strong>
                      )}
                      <div style={{ paddingLeft: 4, color: 'var(--text-tertiary)', fontSize: 12, marginTop: 2 }}>{f.justificacion}</div>
                    </div>
                  ))}
                  {isStaff && (
                    <p style={{ fontSize: 11, color: 'var(--text-tertiary)', margin: '4px 0 0', fontStyle: 'italic' }}>
                      Parámetros de calibración (%, fuente, id): visibles solo para staff Ecosoft.
                    </p>
                  )}
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="primary" onClick={() => setMemoriaOpen(false)}>Cerrar</Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {procedimientoOpen && (
        <ProcedimientoDrawer
          conceptId={concepto.id}
          variante={PROCEDIMIENTO_VARIANTE_DEFAULT}
          clave={concepto.clave}
          descripcion={concepto.descripcion}
          onClose={() => setProcedimientoOpen(false)}
        />
      )}
    </aside>
  );
}
